import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Form, FormikProvider, useFormik } from "formik";
import { styled } from "@mui/material";
import { convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import { useMessageBalance, useSendMessage } from "stores";
import { FormAutocomplete, FormRichTextInput, FormTextInput } from "form";
import { schemas } from "schemas";
import { toolbars } from "utils/toolbarConfigs";
import { toaster } from "utils/toaster";
import { Button } from "components";
import MessageRecipients from "./MessageRecipients";
import { useSmsLength } from "./useSmsLength";
const MessageForm = styled(Form)({
  padding: "1.5rem",
  display: "flex",
  flexDirection: "column",
  gap: "1.5rem",
  fontSize: "14px",
  "& .subject-input": {
    "& label": {
      fontSize: "14px",
      color: "#4D4D4D",
    },
  },
  "& .recipients-wrapper": {
    "& .names": {
      fontWeight: 700,
    },
  },
  "& .message-input": {
    "& .label": {
      fontSize: "14px",
      color: "#4D4D4D",
    },
    "& .richTextEditor-editor": {
      maxHeight: "200px",
    },
  },
  "& .send-btn": {
    alignSelf: "flex-end",
    width: "max-content",
  },
});

const MAX_SMS_CHARS = 160;

const NewMessageForm = ({
  messageType,
  recipients = [],
  recipientOptions = [],
  onSubmit,
}) => {
  const { t } = useTranslation();
  const isEmail = messageType === "email";
  const { send } = useSendMessage();

  const { balance } = useMessageBalance();
  const availableSms = balance?.smsBalance || 0;

  const formik = useFormik({
    initialValues: {
      recipients: recipients
        .map((recipient) => {
          const foundRecipient = recipientOptions.find(
            (rec) => rec.value === recipient
          );
          return foundRecipient;
        })
        .filter((rec) => Boolean(rec)),
      subject: "",
      message: "",
    },
    validationSchema: isEmail
      ? schemas.newEmail(t)
      : schemas.newSms(t, availableSms, MAX_SMS_CHARS),
    onSubmit: async (values) => {
      // Handle manual message length error
      if (messageLengthError) {
        return;
      }

      // Handle rich text editor content
      const editorContent = values.message.getCurrentContent();
      let text = editorContent.getPlainText();
      if (isEmail) {
        const raw = convertToRaw(editorContent);
        const rawTrimmed = {
          ...raw,
          blocks: raw.blocks.map((block) => {
            return { ...block, text: block.text.trim() };
          }),
        };
        text = draftToHtml(rawTrimmed);
      }

      // Handle payload
      const payload = {
        ...values,
        channel: messageType,
        message: text,
      };

      try {
        await send(payload);
        toaster.success({ message: t("NewMessageModal.Success") });
      } catch {
        toaster.error({ message: t("NewMessageModal.Error") });
      } finally {
        onSubmit();
      }
    },
  });

  const translationTypeKey = isEmail ? "Email" : "Sms";
  const recipientsNames = useMemo(() => {
    return formik.values?.recipients?.map((recipient) => recipient.label);
  }, [formik.values?.recipients]);

  const { error: messageLengthError, length: messageLength } = useSmsLength({
    value: formik.values?.message,
    recipientOptions,
    messageType,
    maxLength: MAX_SMS_CHARS,
  });

  const hasNotEnoughBalance = useMemo(() => {
    if (messageType === "email") {
      return false;
    }
    return recipientsNames.length > availableSms || availableSms === 0;
  }, [recipientsNames, availableSms, messageType]);

  return (
    <FormikProvider value={formik}>
      <MessageForm onSubmit={formik.handleSubmit}>
        <FormTextInput
          name="subject"
          label={t(`NewMessageModal.${translationTypeKey}.Subject.Label`)}
          shouldHaveErrorBehaviorAfterSubmit
          className="subject-input"
        />
        <div className="recipients-wrapper">
          <MessageRecipients
            isEmail={isEmail}
            recipientsNames={recipientsNames}
            balance={balance}
          />
          <FormAutocomplete
            name="recipients"
            options={recipientOptions}
            isMultiple
            className="names"
          />
        </div>
        <FormRichTextInput
          name="message"
          toolbar={isEmail ? toolbars.email : toolbars.sms}
          label={t("NewMessageModal.Message.Label")}
          shouldHaveErrorBehaviorAfterSubmit
          type={messageType}
          {...(messageLengthError
            ? {
                helperText: messageLengthError,
                isError: Boolean(messageLengthError),
              }
            : {})}
          charCount={messageLength}
          maxChars={isEmail ? 0 : MAX_SMS_CHARS}
          className="message-input"
        />
        <Button
          type="submit"
          variant="filled"
          isDisabled={hasNotEnoughBalance || formik.isSubmitting}
          isLoading={formik.isSubmitting}
          className="send-btn"
        >
          {t("NewMessageModal.SendBtn")}
        </Button>
      </MessageForm>
    </FormikProvider>
  );
};

export default NewMessageForm;
